import React, { useState, useEffect, useContext, createContext } from 'react';
import { User } from 'firebase/auth';
import { auth } from '../config/firebase';
import { User as AppUser } from '../types';
import apiService from '../services/api';
import smsService from '../services/sms';

interface AuthContextType {
  user: AppUser | null;
  firebaseUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  sendOTP: (phoneNumber: string) => Promise<string>;
  verifyOTP: (verificationId: string, code: string) => Promise<boolean>;
  resendOTP: (phoneNumber: string) => Promise<string>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('authToken');
      
      if (token) {
        try {
          const userData = await apiService.getMyProfile();
          setUser(userData);
        } catch (error) {
          console.error('Error fetching user data:', error);
          localStorage.removeItem('authToken');
          setUser(null);
        }
      }
      
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await apiService.login(email, password);
      
      if (response.success) {
        const token = response.token;
        localStorage.setItem('authToken', token);
        setUser(response.user);
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await auth.signOut();
      localStorage.removeItem('authToken');
      setUser(null);
      setFirebaseUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const sendOTP = async (phoneNumber: string): Promise<string> => {
    try {
      const verificationId = await smsService.sendOTP(phoneNumber);
      return verificationId;
    } catch (error) {
      console.error('Send OTP error:', error);
      throw error;
    }
  };

  const verifyOTP = async (verificationId: string, code: string): Promise<boolean> => {
    try {
      const isValid = await smsService.verifyOTP(verificationId, code);
      
      if (isValid && user) {
        await apiService.verifyPhone(user.id, code);
        await refreshUser();
      }
      
      return isValid;
    } catch (error) {
      console.error('Verify OTP error:', error);
      return false;
    }
  };

  const resendOTP = async (phoneNumber: string): Promise<string> => {
    try {
      const verificationId = await smsService.resendOTP(phoneNumber);
      return verificationId;
    } catch (error) {
      console.error('Resend OTP error:', error);
      throw error;
    }
  };

  const refreshUser = async () => {
    if (firebaseUser) {
      try {
        const userData = await apiService.getMyProfile();
        setUser(userData);
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }
    }
  };

  const value = {
    user,
    firebaseUser,
    loading,
    login,
    logout,
    sendOTP,
    verifyOTP,
    resendOTP,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};